import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { isArray } from 'lodash';

interface ShipmentForecastByContainerState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;


    filter: {
        dZoneGroups: any;
        targetDates: Date;
    };

    items: any[];
    data: any[];
    weeks: any[];
}

const state: ShipmentForecastByContainerState = {
    loading: false,
    loaded: false,
    fatalError: false,

    filter: {
        dZoneGroups: null,
        targetDates: new Date(),
    },

    items: [],
    data: [],
    weeks: [],
};

function buildFilter(params: any) {
    const queryParams: any = {}

    if (params.targetDates) {
        queryParams.date_from = moment(params.targetDates).startOf('month').format('YYYY-MM-DD');
        queryParams.date_to = moment(params.targetDates).startOf('month').add(1, 'month').subtract(1, 'day').format('YYYY-MM-DD')
    }
  
    if (params.dZoneGroups) {
        queryParams.delivery_zone_id = params.dZoneGroups;
    }

    if (params.sortField !== null) {
        queryParams.order_by = params.sortField;
        queryParams.order_type = params.sortDirection;
    }

    return queryParams;
}

export const REPORT_DATA = 'REPORT_DATA';

export const actions: ActionTree<ShipmentForecastByContainerState, RootState> = {

    loadData({ commit, state, dispatch }, params) {
        const vm = (this as any)._vm;

        const queryParams: any = {}

        if (params.targetDates) {
            queryParams.date_from = moment(params.targetDates).startOf('month').format('YYYY-MM-DD');
            queryParams.date_to = moment(params.targetDates).startOf('month').add(1, 'month').subtract(1, 'day').format('YYYY-MM-DD')
        }
        if (params.employee_id) {
            queryParams.employee_id = params.employee_id
        }
        if (params.client_name  ) {
            queryParams.client_name  = params.client_name    
        }
        if (params.client_id  ) {
            queryParams.client_id  = params.client_id
        }
        if (params.with_archived  ) {
            queryParams.with_archived  = params.with_archived
        }

        return new Promise((resolve, reject) => {

            if (Object.values(params).length > 0) {
                commit('START_LOADING');

                vm.$db([
                    vm.$q(REPORT_DATA)
                        .raw('shipment_forecast_by_container')
                        .action(queryParams),
                ]).then(((result: ServerResult) => {
                    commit('SET_REPORT_DATA', result.getOperation(REPORT_DATA).getContent());
                    commit('SET_ITEMS', null);
                    commit('LOADED_SUCCESS');
    
                    resolve();
                }));
            } else {
                resolve();
            }
        })
    },

    exportData({state, getters}, params) {
        const date = moment(params.targetDates);
        return new Promise((resolve, reject) => {
            const csvContent: any[] = [];
            const header = [
                "Продукт",
                `План ${moment(date).format('MM.YYYY')}`,
                `Факт ${moment(date).format('MM.YYYY')}`,
            ]
            const subheader = [
                '',
                '',
                '',
            ]
            const sumheader = [
                'ИТОГО',
                getters.total.month_plan,
                getters.total.month_quantity,
            ]
            state.weeks.forEach((week: any) => {
                header.push(`${moment(week.week_start).format('MM.YYYY')} - ${moment(week.week_end).endOf('week').format('MM.YYYY')}`);
                header.push('');
                subheader.push('План');
                subheader.push('Факт');
                sumheader.push(getters.total.weeks[week.key].plan);
                sumheader.push(getters.total.weeks[week.key].fact);
            })

            csvContent.push(header);
            csvContent.push(subheader);
            csvContent.push(sumheader);
            state.items.forEach((item: any) => {
                if (params.no_products && item.class_name !== 'client-row') {
                    return
                }
                const csv_row = [
                    item.name,
                    item.month_plan,
                    item.month_quantity,
                ]

                state.weeks.forEach((week: any) => {
                    csv_row.push(item.weeks[week.key].plan);
                    csv_row.push(item.weeks[week.key].fact);
                })
                csvContent.push(csv_row);

                if (item.containers) {
                    item.containers.forEach((product: any) => {
                        const csv_row = [
                            product.product_name,
                            product.month_plan,
                            product.month_quantity,
                        ]

                        state.weeks.forEach((week: any) => {
                            csv_row.push(product.weeks[week.key].plan);
                            csv_row.push(product.weeks[week.key].fact);
                        })
                        csvContent.push(csv_row);
                    })
                }
            })

            resolve(csvContent)
        })
    },

    setFilter({commit}, term) {
        commit('SET_ITEMS', term);
    }


};

export const mutations: MutationTree<ShipmentForecastByContainerState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_REPORT_DATA(state, data) {
        state.data = data.documents;
    },

    SET_ITEMS(state, term) {
        const weeks: any = {}
        state.items = state.data.reduce((acc: any[], item: any) => {
            if (term && item.name.toLowerCase().includes(term.toLowerCase())) {
                return acc
            }
            item.weeks = {}
            acc.push( {
                id: item.product_id,
                folded: false,
                name: item.name,
                class_name: 'client-row',
                month_plan: item.month_plan,
                prev_year_quantity: item.prev_year_quantity,
                prev_1_quantity: item.prev_1_quantity,
                prev_2_quantity: item.prev_2_quantity,
                prev_3_quantity: item.prev_3_quantity,
                month_quantity: item.month_quantity,
                prev_avg_quantity: (parseFloat(item.prev_1_quantity) + parseFloat(item.prev_2_quantity) + parseFloat(item.prev_3_quantity)) / 3,
                zones: item.zones,
                weeks: item.weeks
            })
            item.zones = item.zones.map((zone: any) => {
                zone.weeks = {}
                acc.push({
                    id: `${zone.id}-${item.product_id}`,
                    name: zone.name,
                    folded: false,
                    class_name: 'address-row',
                    prev_year_quantity: zone.prev_year_quantity,
                    prev_1_quantity: zone.prev_1_quantity,
                    prev_2_quantity: zone.prev_2_quantity,
                    prev_3_quantity: zone.prev_3_quantity,
                    month_quantity: zone.month_quantity,
                    month_plan: zone.month_plan,
                    prev_avg_quantity: (parseFloat(zone.prev_1_quantity) + parseFloat(zone.prev_2_quantity) + parseFloat(zone.prev_3_quantity)) / 3,
                    weeks: zone.weeks,
                    containers: zone.containers.map((container: any) => {
                        if (isArray(container.weeks)) {
                            container.weeks = container.weeks.reduce((acc: any, p: any) => {
                                if (p.year) {
                                    const key = `${p.year}.${p.week_num}`
                                    p.key = key
                                    if (!weeks[`${key}`]) {
                                        weeks[`${key}`] = {
                                            key: key,
                                            week_start: p.week_start,
                                            week_end: p.week_end,
                                        }
                                    }
                                    if (!item.weeks[`${key}`]) {
                                        item.weeks[`${key}`] = {plan: 0, coef: 0, fact: 0}
                                    }
                                    if (!zone.weeks[`${key}`]) {
                                        zone.weeks[`${key}`] = {plan: 0, coef: 0, fact: 0}
                                    }
                                    p.plan = parseFloat(p.plan) || 0
                                    p.fact = parseFloat(p.fact) || 0
                                    item.weeks[`${key}`].plan += p.plan
                                    item.weeks[`${key}`].fact += p.fact
                                    zone.weeks[`${key}`].plan += p.plan
                                    zone.weeks[`${key}`].fact += p.fact
                                    acc[key] = p
                                }
                                return acc
                            }, {})
                        }
                        return container
                    })
                })
                return zone
            })
            return acc
        }, [])
        if (weeks) {
            state.weeks = Object.values(weeks).sort((a: any, b: any) => {
                return a.key - b.key;
            })
        }
    },
};

const getters = {
    total:(state: any) => {
        const total: any = {
            month_plan: 0,
            prev_year_quantity: 0,
            prev_1_quantity: 0,
            prev_2_quantity: 0,
            prev_3_quantity: 0,
            month_quantity: 0,
            weeks: {}
        }
        state.items.forEach((i: any) => {
            if (i.class_name === 'client-row') {
                total.month_plan += parseFloat(i.month_plan) || 0
                total.prev_year_quantity += parseFloat(i.prev_year_quantity) || 0
                total.prev_1_quantity += parseFloat(i.prev_1_quantity) || 0
                total.prev_2_quantity += parseFloat(i.prev_2_quantity) || 0
                total.prev_3_quantity += parseFloat(i.prev_3_quantity) || 0
                total.month_quantity += parseFloat(i.month_quantity) || 0
                Object.keys(i.weeks).forEach((key: any) => {
                    if (!total.weeks[key]) {
                        total.weeks[key] = {plan: 0, coef: 0, fact: 0}
                    }
                    total.weeks[key].plan += parseFloat(i.weeks[key].plan) || 0
                    total.weeks[key].fact += parseFloat(i.weeks[key].fact) || 0
                })
            }
        })
        total.prev_avg_quantity = (total.prev_1_quantity + total.prev_2_quantity + total.prev_3_quantity) / 3
        return total;
    }
};

export const STORE_KEY = '$_shipment_forecast_by_container';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
